import React, { useState, useEffect, useCallback, useRef } from 'react';
import { MessageSquare, Loader2, CornerUpLeft, ChevronDown, CheckCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { UserConversation } from '@/components/Translation/UserConversation';
import { apiManager } from '@/services/implementations/APIManager';
import type { ChatSessionWithPreviewTurns, ConversationTurn } from '@/types/backend';
import { toast } from 'sonner';
import { useWebRTCSession } from '@/contexts/WebRTCSessionContext';
import { useI18n } from '@/contexts/I18nContext';
import { formatDate as formatDateI18n } from '@/i18n';

interface ChatTurnsPageProps {
  session: ChatSessionWithPreviewTurns;
  onBack: () => void;
}

export const ChatTurnsPage: React.FC<ChatTurnsPageProps> = ({ session, onBack }) => {
  const { t, currentLanguage } = useI18n();

  const [fullTurns, setFullTurns] = useState<ConversationTurn[]>([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMoreTurns, setHasMoreTurns] = useState(false);
  const [totalTurns, setTotalTurns] = useState(0);
  const [initialLoading, setInitialLoading] = useState(true);
  
  // 🔑 添加滚动容器引用，用于保持滚动位置
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const { injectConversationTurns, clearInjectedConversations } = useWebRTCSession();

  // 🔑 加载指定会话的turns - 移除fullTurns.length依赖，避免循环更新
  const loadSessionTurns = useCallback(async (page: number, isAppend = false) => {
    try {
      if (!isAppend) setInitialLoading(true);
      setLoading(true);
      
      // 🔑 记录当前滚动位置（仅在追加模式下）
      let previousScrollHeight = 0;
      if (isAppend && scrollContainerRef.current) {
        previousScrollHeight = scrollContainerRef.current.scrollHeight;
      }
      
      console.log(`📖 加载第 ${page} 页 turns，追加模式: ${isAppend}`);
      
      const response = await apiManager.getSessionTurns(session.id, page, 5);
      
      if (isAppend) {
        // 追加数据（手动加载更多）
        setFullTurns(prev => [...prev, ...response.turns]);
      } else {
        // 重置数据（首次加载）
        setFullTurns(response.turns);
        setCurrentPage(1);
      }
      
      setTotalTurns(response.pagination.total);
      setHasMoreTurns(response.pagination.page < response.pagination.total_pages);
      setCurrentPage(response.pagination.page);
      
      console.log('📖 turns加载完成:', {
        currentPage: response.pagination.page,
        totalPages: response.pagination.total_pages,
        totalTurns: response.pagination.total,
        loadedTurns: isAppend ? '追加模式' : response.turns.length,
        hasMore: response.pagination.page < response.pagination.total_pages
      });
      
      // 🔑 恢复滚动位置（仅在追加模式下）
      if (isAppend && scrollContainerRef.current) {
        // 使用 setTimeout 确保 DOM 更新完成后再调整滚动位置
        setTimeout(() => {
          if (scrollContainerRef.current) {
            const newScrollHeight = scrollContainerRef.current.scrollHeight;
            const scrollDiff = newScrollHeight - previousScrollHeight;
            const currentScrollTop = scrollContainerRef.current.scrollTop;
            
            // 保持相对于之前内容的滚动位置
            scrollContainerRef.current.scrollTop = currentScrollTop;
            
            console.log('📍 保持滚动位置:', {
              previousScrollHeight,
              newScrollHeight,
              scrollDiff,
              currentScrollTop
            });
          }
        }, 10);
      }
      
    } catch (err) {
      console.error('Failed to load session turns:', err);
      toast.error(t('chat.loadingConversation'));
    } finally {
      setLoading(false);
      setInitialLoading(false);
    }
  }, [session.id]); // 🔑 只依赖session.id，避免依赖循环

  // 🔑 手动加载更多turns
  const handleLoadMore = useCallback(() => {
    if (hasMoreTurns && !loading) {
      console.log('🔄 手动触发加载更多，加载下一页:', currentPage + 1);
      loadSessionTurns(currentPage + 1, true);
    }
  }, [hasMoreTurns, loading, currentPage, loadSessionTurns]);

  // 🔑 初始化加载 - 移除loadSessionTurns依赖，使用独立的effect
  useEffect(() => {
    if (session.total_turns_count > 0) {
      console.log('🔄 初始化加载会话turns:', session.id);
      setFullTurns([]);
      setCurrentPage(1);
      setHasMoreTurns(false);
      setTotalTurns(0);
      
      // 🔑 直接调用API，避免依赖loadSessionTurns函数
      const initializeLoad = async () => {
        try {
          setInitialLoading(true);
          const response = await apiManager.getSessionTurns(session.id, 1, 5);
          
          setFullTurns(response.turns);
          setTotalTurns(response.pagination.total);
          setHasMoreTurns(response.pagination.page < response.pagination.total_pages);
          setCurrentPage(response.pagination.page);
          
          console.log('🔄 初始化加载完成:', {
            sessionId: session.id,
            turnsLoaded: response.turns.length,
            totalTurns: response.pagination.total,
            hasMore: response.pagination.page < response.pagination.total_pages
          });
        } catch (err) {
          console.error('Failed to initialize session turns:', err);
          toast.error(t('chat.loadingConversation'));
        } finally {
          setInitialLoading(false);
        }
      };
      
      initializeLoad();
    } else {
      setInitialLoading(false);
    }
    
    // 清理函数
    return () => {
      clearInjectedConversations();
    };
  }, [session.id, session.total_turns_count, clearInjectedConversations]); // 🔑 移除loadSessionTurns依赖

  // 🔑 当fullTurns更新时，注入到WebRTC context
  useEffect(() => {
    if (fullTurns.length > 0) {
      console.log('🔄 注入完整对话数据:', fullTurns.length);
      // 注意：后端返回的turns是按时间升序排列，需要重排来显示最新的在下面
      const turns = [...fullTurns].sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());
      injectConversationTurns(turns); 
    }
  }, [fullTurns, injectConversationTurns]);

  const formatDate = (dateString: string) => {
    return formatDateI18n(dateString, currentLanguage);
  };

  return (
    <div className="h-full flex flex-col">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6 px-3 pt-3">
        <Button
          onClick={onBack}
          variant="ghost"
          size="sm"
          className="gap-2"
        >
          <CornerUpLeft className="h-4 w-4" />
          {/* 返回列表 */}
        </Button>
        <div className="flex-1 min-w-0">
          <h1 className="text-xl font-semibold max-w-[250px] truncate sm:max-w-none sm:whitespace-normal sm:truncate-none transition-[max-width] duration-200">
            {session.title}
          </h1>
          <div className="flex items-center gap-4 text-sm text-muted-foreground">
            <span>{formatDate(session.created_at)}</span>
            <span>{t('chat.totalTurns').replace('{count}', session.total_turns_count.toString())}</span>
            {fullTurns.length > 0 && fullTurns.length < session.total_turns_count && (
              <span>{t('chat.loadedTurns').replace('{count}', fullTurns.length.toString())}</span>
            )}
          </div>
        </div>
      </div>

      {/* Conversation Content */}
      <div 
        ref={scrollContainerRef}
        className="flex-1 overflow-y-auto"
      >
        {session.total_turns_count === 0 ? (
          <div className="text-center py-12 text-muted-foreground px-4">
            <MessageSquare className="h-12 w-12 mx-auto mb-4 opacity-50" />
            <p>{t('chat.noContent')}</p>
          </div>
        ) : initialLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            <span className="ml-2 text-muted-foreground">{t('chat.loadingConversation')}</span>
          </div>
        ) : (
          <div className="relative">
            {/* 对话内容 */}
            <UserConversation />
            
            {/* 手动加载更多按钮 */}
            {hasMoreTurns && (
              <div className="flex justify-center py-6 px-4">
                <Button
                  onClick={handleLoadMore}
                  variant="outline"
                  className="gap-2 min-w-32"
                  disabled={loading}
                >
                  {loading ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" />
                      <span>{t('common.loading')}</span>
                    </>
                  ) : (
                    <>
                      <ChevronDown className="h-4 w-4" />
                      <span>{t('chat.loadMore')} ({t('chat.remainingTurns').replace('{count}', (totalTurns - fullTurns.length).toString())})</span>
                    </>
                  )}
                </Button>
              </div>
            )}

            {/* 加载完成提示 */}
            {!hasMoreTurns && fullTurns.length > 0 && fullTurns.length === totalTurns && (
              <div className="text-center py-6 text-muted-foreground">
                <div className="text-sm bg-muted/30 rounded-lg px-4 py-2 inline-flex items-center gap-2">
                    <CheckCircle className="h-4 w-4" />
                    {t('chat.allLoadedMessage').replace('{count}', totalTurns.toString())}
                </div>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}; 